<template>
    <div class="drawer-sections">
        <div>
            <div class="casc-head-row">
                <div class="casc-head-meta">
                    <div class="dr-sec-h">Каналы и приоритеты</div>
                    <div class="casc-updated">{{ cascadeRefreshLabel }}</div>
                </div>
                <button
                    type="button"
                    class="casc-refresh"
                    title="Обновить статусы каналов"
                    aria-label="Обновить статусы каналов"
                    :disabled="isStatusRefreshing"
                    @click="handleManualRefresh"
                >
                    <SpinArrowsIcon
                        class="casc-refresh__icon"
                        :class="{ 'casc-refresh__icon--spinning': isStatusRefreshing }"
                    />
                </button>
            </div>
            <div v-if="showCascadeTabs" class="casc-tabs" role="tablist" aria-label="Каскады проекта">
                <button
                    v-for="tab in cascadeTabs"
                    :key="tab.id"
                    type="button"
                    role="tab"
                    class="casc-tab"
                    :class="{ 'casc-tab--active': tab.id === String(selectedCascadeId) }"
                    :aria-selected="tab.id === String(selectedCascadeId) ? 'true' : 'false'"
                    :disabled="initialLoading || refreshing"
                    :title="tab.label"
                    @click="selectCascade(tab.id)"
                >{{ tab.label }}</button>
            </div>
            <div class="casc-list-wrap">
                <div v-if="initialLoading" class="casc-loading">
                    <v-progress-circular indeterminate size="36" width="3" color="mainNew" />
                    <span class="casc-loading__label">Загружаем каналы...</span>
                </div>
                <template v-else>
                    <div v-if="!channels.length" class="casc-empty">
                        {{ emptyStateText }}
                    </div>
                    <div v-else class="casc-list-host">
                        <div class="casc-table-head">
                            <span>Канал</span>
                            <span>Приоритет</span>
                        </div>
                        <div class="casc-list" :class="{ 'casc-list--dimmed': statusesRefreshing }">
                            <div
                                v-for="channel in channels"
                                :key="channel.connectorId"
                                :class="[
                                    'casc-row',
                                    {
                                        'not-connected':
                                            !isChannelAuthUiLoading(channel) &&
                                            !isChannelAuthorized(channel),
                                        inactive: !channel.cascadeEnabled,
                                    },
                                ]"
                            >
                                <span class="casc-ch-ico" :style="{ background: channel.bg }">
                                    <v-img
                                        v-if="channel.image"
                                        transition="slide-x-transition"
                                        :src="channel.image"
                                        class="casc-ch-ico__img"
                                    />
                                    <span v-else-if="channel.symbol" class="casc-ch-ico__symbol">
                                        {{ channel.symbol }}
                                    </span>
                                    <v-icon v-else size="18" color="#fff">{{ channel.mdiIcon }}</v-icon>
                                </span>
                                <div class="casc-ch">
                                    <div class="casc-ch-head">
                                        <div class="casc-ch-n">{{ channel.displayName }}</div>
                                        <v-progress-circular
                                            v-if="isChannelAuthUiLoading(channel)"
                                            indeterminate
                                            size="14"
                                            width="2"
                                            color="mainNew"
                                            class="casc-auth-loading"
                                        />
                                        <span
                                            v-else-if="isChannelAuthorized(channel)"
                                            class="casc-status ok"
                                            title="Канал подключён"
                                            aria-label="Канал подключён"
                                        >✓</span>
                                        <template v-else>
                                            <span
                                                class="casc-status bad"
                                                title="Канал не подключён. Нажмите QR-код и отсканируйте канал"
                                                aria-label="Канал не подключён"
                                            >!</span>
                                            <button
                                                v-if="
                                                    shouldShowQrCodeButton(channel) &&
                                                    !isChannelAuthUiLoading(channel)
                                                "
                                                type="button"
                                                class="casc-qr-btn"
                                                :title="`Показать QR-код для подключения ${channel.displayName}`"
                                                :aria-label="`Показать QR-код для подключения ${channel.displayName}`"
                                                :disabled="isStatusRefreshing"
                                                @click="openChannelQr(channel)"
                                            >
                                                <v-icon size="15">mdi-qrcode-scan</v-icon>
                                            </button>
                                        </template>
                                    </div>
                                </div>
                                <div class="casc-pri">
                                    <PrioritySelect
                                        :value="isPriorityLockedForChannel(channel) ? null : (channel.cascadeEnabled ? channel.priority : null)"
                                        :max-priority="maxCascadePriority"
                                        :disabled="isStatusRefreshing || isPriorityLockedForChannel(channel)"
                                        :tooltip="getPriorityLockTooltip(channel)"
                                        show-dropdown-icon
                                        @input="handleChannelPriorityChange(channel, $event)"
                                    />
                                </div>
                            </div>
                        </div>
                        <transition name="casc-overlay-fade">
                            <div v-if="refreshing" class="casc-loading-overlay" aria-hidden="true">
                                <v-progress-circular indeterminate size="32" width="3" color="mainNew" />
                            </div>
                        </transition>
                    </div>
                </template>
            </div>
            <button
                v-if="canConnectChannel"
                type="button"
                class="ch-add-btn"
                :disabled="initialLoading || refreshing"
                @click="connectChannel"
            >
                <v-icon class="ch-add-btn__icon" size="18" color="mainNew">mdi-plus</v-icon>
                Подключить канал
            </button>
        </div>
        <StarterPackChannelQrModal
            v-model="qrModalOpen"
            :channel="qrModalChannel"
            :project-id="projectId"
            @authorized="handleQrAuthorized"
        />
        <MaxMarketingRiskModal
            :value="maxRiskModalOpen"
            :context="maxRiskContext"
            @accept="acceptMaxRisk"
            @remove="removeMaxFromMailings"
            @dismiss="dismissMaxRisk"
        />
        <div class="dr-divider"></div>
        <div>
            <div class="dr-sec-h">Тонкая настройка</div>
            <p class="dr-sec-p">Расширенные параметры каскада доступны на главной странице системы.</p>
            <a class="dr-link-btn" :href="fineTuneUrl" target="_blank" rel="noopener">
                Открыть настройку каскада
                <v-icon class="dr-link-btn__icon" size="13" color="white">mdi-arrow-right</v-icon>
            </a>
        </div>
    </div>
</template>

<script>
import {
    deleteCascadeChannel,
    getCascadeById,
    getCascadesByProject,
    getSlots,
    updateCascadeChannelsPriorities,
} from '@/api/networkFixer'
import api from '@root/src/api/api'
import PrioritySelect from '@/components/project/PrioritySelect.vue'
import MaxMarketingRiskModal from '@/components/project/MaxMarketingRiskModal.vue'
import { isRegularMaxChannel } from '@/helpers/channels/isRegularMaxChannel'
import SpinArrowsIcon from '@/assets/vuetifyIcons/spinArrows.vue'
import { openChannelCreationSidebar } from '@/helpers/channels/openChannelCreationSidebar'
import { ensureProjectBillingLoaded } from '@/helpers/channels/ensureProjectBillingLoaded'
import { checkRole } from '@root/src/helpers/roles'
import {
    fetchReworkAuthStatus,
    isAuthStatusLoading as getIsAuthStatusLoading,
    isChannelAuthorized as getIsChannelAuthorized,
    resolveConnectorAuthItem,
    shouldShowQrCodeButton as getShouldShowQrCodeButton,
    shouldUseReworkAuthStatus,
} from '@/helpers/channels/channelAuthStatus'
import {
    buildInactiveSlotConnectItem,
    getFirstAvailableSlot,
    hasAvailableChannelSlots,
} from '@/utils/onboardingHelpers'
import { PROVIDER_TYPES } from '@root/src/vars/general'
import { buildCascadeChannelItems } from '../../utils/cascadeChannels'
import { hasReadyMarketingChannel } from '../../utils/cascadeReadiness'
import { buildCascadeTabs, getDefaultCascadeTabId } from '../../utils/cascadeTabs'
import StarterPackChannelQrModal from './StarterPackChannelQrModal.vue'

export default {
    name: 'StarterPackDrawerCascade',
    components: {
        PrioritySelect,
        SpinArrowsIcon,
        StarterPackChannelQrModal,
        MaxMarketingRiskModal,
    },
    props: {
        projectId: {
            type: String,
            required: true,
        },
        fineTuneUrl: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            initialLoading: false,
            refreshing: false,
            cascades: [],
            selectedCascadeId: null,
            channelSlots: [],
            channelAuthStatusByConnectorId: {},
            channelAuthStatusLoadingByConnectorId: {},
            qrModalOpen: false,
            qrModalChannel: null,
            maxRiskModalOpen: false,
            maxRiskPending: null,
            maxRiskContext: '',
            lastRefreshLabel: '',
            statusesRefreshing: false,
        }
    },
    computed: {
        isStatusRefreshing() {
            return (
                this.statusesRefreshing ||
                this.refreshing ||
                this.initialLoading ||
                Object.values(this.channelAuthStatusLoadingByConnectorId).some(Boolean)
            )
        },
        cascadeRefreshLabel() {
            if (this.isStatusRefreshing) {
                return 'обновляем статусы…'
            }
            if (this.lastRefreshLabel) {
                return `статусы обновлены: ${this.lastRefreshLabel}`
            }
            return 'статусы обновляются после нажатия'
        },
        canConnectChannel() {
            return checkRole(this.projectId, 'project:connector:edit') && hasAvailableChannelSlots(this.channelSlots)
        },
        cascadeTabs() {
            return buildCascadeTabs(this.cascades)
        },
        showCascadeTabs() {
            return this.cascadeTabs.length > 1
        },
        selectedCascade() {
            return (
                this.cascades.find(cascade => String(cascade?.id) === String(this.selectedCascadeId)) ||
                null
            )
        },
        channelItemsByCascadeId() {
            const project = this.$store.getters.projectById(this.projectId) || {}
            const base = {
                connectors: project.connectors || [],
                connectorsAvatars: this.$store.state.connectorsAvatars,
                slots: this.channelSlots,
            }

            if (!this.cascadeTabs.length) {
                return { none: buildCascadeChannelItems({ ...base, cascade: null }) }
            }

            const itemsByCascadeId = {}
            this.cascadeTabs.forEach(tab => {
                itemsByCascadeId[tab.id] = buildCascadeChannelItems({ ...base, cascade: tab.cascade })
            })
            return itemsByCascadeId
        },
        channels() {
            if (!this.cascadeTabs.length) {
                return this.channelItemsByCascadeId.none || []
            }
            return this.channelItemsByCascadeId[String(this.selectedCascadeId)] || []
        },
        mailingsChannels() {
            const mailingsTab = this.cascadeTabs.find(tab => tab.cascade?.cascadeType === 'mailings')
            if (!mailingsTab) {
                return []
            }
            return this.channelItemsByCascadeId[mailingsTab.id] || []
        },
        isCascadeReady() {
            return hasReadyMarketingChannel(this.mailingsChannels, channel =>
                this.isChannelAuthorized(channel),
            )
        },
        emptyStateText() {
            if (!this.selectedCascade || this.selectedCascade.cascadeType === 'mailings') {
                return 'Пока нет подключённых каналов. Подключите первый, чтобы рассылки начали работать.'
            }
            return 'В этом каскаде пока нет каналов. Подключите канал и назначьте ему приоритет.'
        },
        maxCascadePriority() {
            return this.channels.reduce((max, channel) => {
                const priority = channel?.priority
                if (priority === null || priority === undefined) {
                    return max
                }
                return Math.max(max, priority)
            }, 0)
        },
    },
    watch: {
        projectId(newProjectId, oldProjectId) {
            if (newProjectId && newProjectId !== oldProjectId) {
                this.refreshOnOpen()
            }
        },
    },
    mounted() {
        this.$root.$on('reload-channel-slots', this.handleSlotsReload)
        this.$root.$on('starter-pack-cascade-reload', this.handleCascadeReload)
    },
    beforeDestroy() {
        this.$root.$off('reload-channel-slots', this.handleSlotsReload)
        this.$root.$off('starter-pack-cascade-reload', this.handleCascadeReload)
    },
    methods: {
        handleSlotsReload() {
            void this.refreshCascadeState()
        },
        handleCascadeReload() {
            void this.refreshCascadeState()
        },
        refreshOnOpen() {
            if (!this.projectId) {
                return Promise.resolve()
            }

            this.channelAuthStatusByConnectorId = {}
            this.channelAuthStatusLoadingByConnectorId = {}
            this.statusesRefreshing = true

            return this.loadChannels({ silent: this.channels.length > 0, awaitAuth: true }).finally(() => {
                this.statusesRefreshing = false
            })
        },
        async handleManualRefresh() {
            if (this.isStatusRefreshing) {
                return
            }

            await this.refreshOnOpen()
            this.lastRefreshLabel = 'только что'
            this.$emit('toast', 'Статусы каналов обновлены')
        },
        async refreshCascadeState() {
            if (!this.projectId) {
                return
            }

            this.channelAuthStatusByConnectorId = {}
            this.channelAuthStatusLoadingByConnectorId = {}
            await this.loadChannels({ silent: true })
        },
        async loadSlots() {
            if (!this.projectId) {
                this.channelSlots = []
                return
            }

            const { data, error } = await getSlots({ projectId: this.projectId })
            this.channelSlots = !error && Array.isArray(data) ? data : []
        },
        async connectChannel() {
            const slot = getFirstAvailableSlot(this.channelSlots)
            if (!slot) {
                return
            }

            const project = this.$store.getters.projectById(this.projectId) || {}
            await ensureProjectBillingLoaded(this.projectId, project)

            openChannelCreationSidebar(this, buildInactiveSlotConnectItem(slot), 'new', {
                context: 'starter-pack',
            })
        },
        async loadChannels({ silent = false, awaitAuth = false } = {}) {
            if (!this.projectId) {
                this.cascades = []
                this.selectedCascadeId = null
                this.emitChannelsState()
                return
            }

            if (!silent) {
                if (!this.channels.length) {
                    this.initialLoading = true
                } else {
                    this.refreshing = true
                }
            }

            try {
                try {
                    await this.$store.dispatch('project/fetchProject', this.projectId)
                } catch (e) {
                    // ignore
                }

                const [{ data: cascades, error }, slotsResult] = await Promise.all([
                    getCascadesByProject(this.projectId),
                    getSlots({ projectId: this.projectId }),
                ])
                if (!slotsResult.error && Array.isArray(slotsResult.data)) {
                    this.channelSlots = slotsResult.data
                } else {
                    this.channelSlots = []
                }
                if (error) {
                    this.$store.dispatch('callNotify', 'Не удалось загрузить каскад каналов')
                    return
                }

                this.cascades = Array.isArray(cascades) ? cascades : []
                const tabs = this.cascadeTabs
                const selectedStillExists = tabs.some(tab => tab.id === String(this.selectedCascadeId))
                if (!selectedStillExists) {
                    this.selectedCascadeId = getDefaultCascadeTabId(tabs)
                }
                if (awaitAuth) {
                    await this.prefetchAuthStatuses()
                } else {
                    this.prefetchAuthStatuses()
                }
            } finally {
                this.initialLoading = false
                if (!silent) {
                    this.refreshing = false
                }
                this.emitChannelsState()
            }
        },
        emitChannelsState() {
            this.$emit('channels-change', this.isCascadeReady)
        },
        selectCascade(cascadeId) {
            if (this.initialLoading || this.refreshing) {
                return
            }

            const nextCascadeId = String(cascadeId)
            if (nextCascadeId === String(this.selectedCascadeId)) {
                return
            }

            this.selectedCascadeId = nextCascadeId
            void this.prefetchAuthStatuses()
        },
        isPriorityLockedForChannel(channel) {
            if (!this.selectedCascade || this.selectedCascade.cascadeType === 'mailings') {
                return false
            }
            return channel?.providerType === PROVIDER_TYPES.whatsapp
        },
        getPriorityLockTooltip(channel) {
            if (!this.isPriorityLockedForChannel(channel)) {
                return ''
            }

            const cascadeType = this.selectedCascade?.cascadeType
            if (cascadeType === 'notifications') {
                return 'Отправка сервисных уведомлений недоступна через бесплатный канал.'
            }
            if (cascadeType === 'crmMessages') {
                return 'Отправка кодов подтверждения из YCLIENTS недоступна через бесплатный канал.'
            }
            return 'Это бесплатный канал, приоритет изменить нельзя.'
        },
        getProjectConnectors() {
            const project = this.$store.getters.projectById(this.projectId) || {}
            return project.connectors || []
        },
        getAuthItem(channel) {
            return resolveConnectorAuthItem(channel, this.getProjectConnectors())
        },
        isAuthStatusLoading(channel) {
            return getIsAuthStatusLoading(this.getAuthItem(channel), this.channelAuthStatusLoadingByConnectorId)
        },
        isChannelAuthUiLoading(channel) {
            if (this.statusesRefreshing) {
                return true
            }

            return this.isAuthStatusLoading(channel)
        },
        isChannelAuthorized(channel) {
            return getIsChannelAuthorized(this.getAuthItem(channel), {
                statusByConnectorId: this.channelAuthStatusByConnectorId,
                loadingByConnectorId: this.channelAuthStatusLoadingByConnectorId,
            })
        },
        shouldShowQrCodeButton(channel) {
            return getShouldShowQrCodeButton(this.getAuthItem(channel))
        },
        async prefetchAuthStatuses() {
            const tasks = []
            const seenConnectorIds = new Set()
            const allChannelItems = Object.values(this.channelItemsByCascadeId).flat()

            allChannelItems.forEach(channel => {
                const item = this.getAuthItem(channel)
                if (!shouldUseReworkAuthStatus(item)) {
                    return
                }

                const connectorId = item.connectorId
                if (
                    !connectorId ||
                    seenConnectorIds.has(connectorId) ||
                    this.channelAuthStatusLoadingByConnectorId[connectorId] ||
                    this.channelAuthStatusByConnectorId[connectorId] !== undefined
                ) {
                    return
                }

                seenConnectorIds.add(connectorId)
                tasks.push(this.fetchReworkAuthStatus(item))
            })

            await Promise.all(tasks)
            this.emitChannelsState()
        },
        async fetchReworkAuthStatus(item) {
            await fetchReworkAuthStatus(item, {
                apiClient: api,
                isLoading: connectorId => Boolean(this.channelAuthStatusLoadingByConnectorId[connectorId]),
                hasStatus: connectorId => this.channelAuthStatusByConnectorId[connectorId] !== undefined,
                setStatus: (connectorId, value) => {
                    this.$set(this.channelAuthStatusByConnectorId, connectorId, value)
                },
                setLoading: (connectorId, value) => {
                    this.$set(this.channelAuthStatusLoadingByConnectorId, connectorId, value)
                },
            })
        },
        openChannelQr(channel) {
            this.qrModalChannel = channel
            this.qrModalOpen = true
        },
        handleQrAuthorized(connectorId) {
            this.$set(this.channelAuthStatusByConnectorId, connectorId, true)
            this.emitChannelsState()
            void this.refreshCascadeState()
        },
        getCascadeActiveChannels() {
            return this.channels.filter(channel => channel.channelId)
        },
        buildNormalizedCascadePriorities(prioritiesCopy, activeChannels) {
            const channelsWithPriorities = activeChannels
                .map(channel => ({
                    id: channel.connectorId,
                    priority: prioritiesCopy[channel.connectorId] ?? null,
                }))
                .filter(item => item.id && item.priority !== null && item.priority !== undefined)
            channelsWithPriorities.sort((a, b) => a.priority - b.priority)

            const normalizedPriorities = {}
            channelsWithPriorities.forEach((item, index) => {
                normalizedPriorities[item.id] = index + 1
            })
            return normalizedPriorities
        },
        async saveCascadePrioritiesBatch(normalizedPriorities, changedConnectorId, cascadeId) {
            const activeChannels = this.getCascadeActiveChannels()
            const toSave = []

            Object.keys(normalizedPriorities).forEach(connectorId => {
                toSave.push({ connectorId, priority: normalizedPriorities[connectorId] })
            })

            if (changedConnectorId && !normalizedPriorities[changedConnectorId]) {
                toSave.push({ connectorId: changedConnectorId, priority: null })
            }

            const updates = []
            const deletions = []
            let cascadeSnapshot = null

            const findActiveChannelByConnectorId = connectorId =>
                activeChannels.find(channel => String(channel.connectorId) === String(connectorId))

            for (const item of toSave) {
                const channel = findActiveChannelByConnectorId(item.connectorId)
                if (!channel?.channelId) {
                    continue
                }

                if (item.priority === null || item.priority === undefined) {
                    deletions.push({ channelId: channel.channelId })
                } else {
                    updates.push({ channelId: channel.channelId, priority: item.priority })
                }
            }

            const isRemovalFlow = changedConnectorId && !normalizedPriorities[changedConnectorId]
            if (isRemovalFlow) {
                const changedChannel = findActiveChannelByConnectorId(changedConnectorId)
                const latestCascade = (await getCascadeById(cascadeId)).data
                const cascadeChannelToDelete = latestCascade?.channels?.find(
                    channel => String(channel.channelId) === String(changedChannel?.channelId),
                )

                if (cascadeChannelToDelete?.id) {
                    const { error } = await deleteCascadeChannel(cascadeChannelToDelete.id)
                    if (error) {
                        throw new Error('pre deletion failed')
                    }
                }
                cascadeSnapshot = (await getCascadeById(cascadeId)).data || null
            }

            if (updates.length > 0) {
                const { error } = await updateCascadeChannelsPriorities({
                    cascadeId,
                    channels: updates,
                })
                if (error) {
                    throw new Error('batch update failed')
                }

                const updatedCascade = await getCascadeById(cascadeId)
                if (updatedCascade.data) {
                    cascadeSnapshot = updatedCascade.data
                }
            }
            if (deletions.length > 0) {
                for (const deletion of deletions) {
                    if (isRemovalFlow && String(deletion.channelId) === String(findActiveChannelByConnectorId(changedConnectorId)?.channelId)) {
                        continue
                    }
                    const latestCascade = cascadeSnapshot || (await getCascadeById(cascadeId)).data
                    const cascadeChannel = latestCascade?.channels?.find(
                        channel => String(channel.channelId) === String(deletion.channelId),
                    )

                    if (cascadeChannel) {
                        const { error } = await deleteCascadeChannel(cascadeChannel.id)
                        if (error) {
                            throw new Error('deletion failed')
                        }
                    }
                }

                const updatedCascade = await getCascadeById(cascadeId)
                if (updatedCascade.data) {
                    cascadeSnapshot = updatedCascade.data
                }
            }
        },
        handleChannelPriorityChange(channel, newPriority) {
            if (this.shouldConfirmMaxPriority(channel, newPriority)) {
                this.maxRiskPending = { channel, priority: newPriority }
                this.maxRiskContext = 'Основной каскад · Маркетинговые рассылки'
                this.maxRiskModalOpen = true
                return
            }
            return this.applyChannelPriorityChange(channel, newPriority)
        },
        shouldConfirmMaxPriority(channel, priority) {
            if (this.maxRiskModalOpen) return false
            if (priority === null || priority === undefined) return false
            if (this.selectedCascade?.cascadeType !== 'mailings') return false
            return isRegularMaxChannel(this.resolveChannelConnector(channel))
        },
        resolveChannelConnector(channel) {
            const connectorId = String(channel?.connectorId || '')
            return (
                this.getProjectConnectors().find(
                    item => String(item?.connectorId || item?.id) === connectorId,
                ) || channel
            )
        },
        acceptMaxRisk() {
            const pending = this.maxRiskPending
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
            if (!pending) return
            return this.applyChannelPriorityChange(pending.channel, pending.priority)
        },
        removeMaxFromMailings() {
            const pending = this.maxRiskPending
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
            if (!pending) return
            return this.applyChannelPriorityChange(pending.channel, null)
        },
        dismissMaxRisk() {
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
        },
        async applyChannelPriorityChange(channel, newPriority) {
            const cascadeId = this.selectedCascadeId
            if (!cascadeId || this.refreshing || !channel?.channelId) {
                return
            }

            const connectorId = channel.connectorId
            const currentPriority = channel.cascadeEnabled ? channel.priority : null
            if (currentPriority === newPriority) {
                return
            }

            const activeChannels = this.getCascadeActiveChannels()
            const prioritiesCopy = {}
            activeChannels.forEach(item => {
                if (item.cascadeEnabled && item.priority !== null && item.priority !== undefined) {
                    prioritiesCopy[item.connectorId] = item.priority
                }
            })

            if (newPriority === null || newPriority === undefined) {
                delete prioritiesCopy[connectorId]
            } else if (currentPriority === null || currentPriority === undefined) {
                const channelWithSamePriority = activeChannels.find(
                    item =>
                        item.connectorId !== connectorId &&
                        item.cascadeEnabled &&
                        prioritiesCopy[item.connectorId] === newPriority,
                )
                if (channelWithSamePriority) {
                    activeChannels.forEach(item => {
                        if (
                            item.connectorId !== connectorId &&
                            item.cascadeEnabled &&
                            prioritiesCopy[item.connectorId] !== undefined &&
                            prioritiesCopy[item.connectorId] >= newPriority
                        ) {
                            prioritiesCopy[item.connectorId] += 1
                        }
                    })
                }
                prioritiesCopy[connectorId] = newPriority
            } else {
                prioritiesCopy[connectorId] = newPriority
                const isMovingUp = newPriority < currentPriority
                if (isMovingUp) {
                    activeChannels.forEach(item => {
                        if (
                            item.connectorId !== connectorId &&
                            item.cascadeEnabled &&
                            prioritiesCopy[item.connectorId] !== undefined &&
                            prioritiesCopy[item.connectorId] >= newPriority &&
                            prioritiesCopy[item.connectorId] < currentPriority
                        ) {
                            prioritiesCopy[item.connectorId] += 1
                        }
                    })
                } else {
                    activeChannels.forEach(item => {
                        if (
                            item.connectorId !== connectorId &&
                            item.cascadeEnabled &&
                            prioritiesCopy[item.connectorId] !== undefined &&
                            prioritiesCopy[item.connectorId] > currentPriority &&
                            prioritiesCopy[item.connectorId] <= newPriority
                        ) {
                            prioritiesCopy[item.connectorId] -= 1
                        }
                    })
                }
            }

            const normalizedPriorities = this.buildNormalizedCascadePriorities(
                prioritiesCopy,
                activeChannels,
            )

            this.refreshing = true
            try {
                await this.saveCascadePrioritiesBatch(normalizedPriorities, connectorId, cascadeId)
                await this.loadChannels({ silent: true })
            } catch (e) {
                this.$store.dispatch('callNotify', 'Не удалось изменить приоритет канала')
                await this.loadChannels({ silent: true })
            } finally {
                this.refreshing = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/vars.sass';

.drawer-sections {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.dr-sec-h {
    margin-bottom: 8px;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13.5px;
    font-weight: 800;
    letter-spacing: -0.01em;
}

.casc-head-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;

    .dr-sec-h {
        margin-bottom: 2px;
    }
}

.casc-head-meta {
    min-width: 0;
}

.casc-updated {
    color: $muted-light-new;
    font-size: 11px;
    line-height: 1.35;
}

.casc-refresh {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    color: $main-new;
    cursor: pointer;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 9px;
    transition: 0.15s;

    &:hover:not(:disabled) {
        background: $bcg-card-new;
        border-color: #c7cdf2;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.7;
        cursor: default;
        transform: none;
    }
}

.casc-refresh__icon {
    display: block;
    width: 16px;
    height: 16px;
    color: $main-new;
}

.casc-refresh__icon--spinning {
    animation: casc-spin 0.8s linear infinite;
    transform-origin: 50% 50%;
}

@keyframes casc-spin {
    to {
        transform: rotate(360deg);
    }
}

.casc-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
}

.casc-tab {
    max-width: 160px;
    padding: 6px 11px;
    overflow: hidden;
    color: $muted-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 11.5px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 999px;
    transition: 0.15s;

    &:hover:not(:disabled):not(.casc-tab--active) {
        background: $bcg-card-new;
        border-color: #c7cdf2;
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
}

.casc-tab--active {
    color: #fff;
    background: $main-new;
    border-color: $main-new;
}

.dr-sec-p {
    margin-bottom: 14px;
    color: $muted-new;
    font-size: 12.5px;
    line-height: 1.5;
}

.dr-divider {
    height: 1px;
    background: $border-new;
    margin: 0;
}

.casc-list-wrap {
    min-height: 88px;
}

.casc-list-host {
    position: relative;
}

.casc-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 120px;
    padding: 24px 13px;
    background: #f6f7f9;
    border: 1px dashed $border-new;
    border-radius: 10px;
}

.casc-loading__label {
    color: $text-color-new;
    font-size: 13px;
}

.casc-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.72);
    border-radius: 10px;
}

.casc-list--dimmed {
    pointer-events: none;
}

.casc-overlay-fade-enter-active,
.casc-overlay-fade-leave-active {
    transition: opacity 0.15s ease;
}

.casc-overlay-fade-enter,
.casc-overlay-fade-leave-to {
    opacity: 0;
}

.casc-list {
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.casc-table-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 6px 2px 0;
    margin-bottom: 2px;
    color: $muted-light-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 9.5px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;

    span:last-child {
        width: 58px;
        text-align: center;
    }
}

.casc-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 11px;
    transition: 0.15s;

    &.not-connected {
        background: #fffbf1;
        border-color: #f3d396;
    }

    &.inactive {
        background: #fafbfd;
    }

    &.not-connected.inactive {
        background: #fffbf1;
    }
}

.casc-ch-ico {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    overflow: hidden;
    border-radius: 8px;
}

.casc-ch-ico ::v-deep .v-icon {
    line-height: 1 !important;
    display: block;
}

.casc-ch-ico__img {
    width: 100%;
    height: 100%;
}

.casc-ch-ico__img ::v-deep .v-image__image {
    background-size: contain !important;
}

.casc-ch-ico__symbol {
    color: #4f43bd;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
}

.casc-ch {
    flex: 1;
    min-width: 0;
}

.casc-ch-head {
    display: flex;
    gap: 7px;
    align-items: center;
    min-width: 0;
}

.casc-ch-n {
    overflow: hidden;
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.casc-auth-loading {
    flex-shrink: 0;
}

.casc-status {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 10px;
    font-weight: 900;
    line-height: 1;
    border-radius: 999px;

    &.ok {
        color: #16a957;
        background: #e6f5ec;
    }

    &.bad {
        color: #fff;
        font-size: 11px;
        background: #d14343;
        border: 1px solid #d14343;
        box-shadow: 0 0 0 3px rgba(209, 67, 67, 0.12);
    }
}

.casc-qr-btn {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    color: #a66a00;
    cursor: pointer;
    background: #fff5db;
    border: 1px solid #f2d18b;
    border-radius: 8px;
    transition: 0.15s;

    &:hover:not(:disabled) {
        background: #ffe9b8;
        border-color: #eab65b;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
}

.casc-qr-btn ::v-deep .v-icon {
    color: #a66a00 !important;
}

.casc-pri {
    position: relative;
    z-index: 1;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: flex-end;
    width: 58px;
}

.casc-pri ::v-deep .priority-select-wrapper {
    gap: 0;
}

.casc-pri ::v-deep .priority-label {
    display: none;
}

.casc-pri ::v-deep .priority-box--disabled {
    background: #f3f5f8;
    cursor: not-allowed;

    &:hover {
        background: #f3f5f8;
    }
}

.casc-pri ::v-deep .priority-number--disabled,
.casc-pri ::v-deep .priority-box--disabled .priority-box--disabled__box {
    color: $muted-light-new;
}

.casc-pri ::v-deep .priority-dropdown-icon--disabled {
    color: $muted-light-new !important;
}

.casc-empty {
    padding: 13px;
    color: $muted-light-new;
    font-size: 12.5px;
    text-align: center;
    background: #f6f7f9;
    border: 1px dashed $border-new;
    border-radius: 10px;
}

.ch-add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    width: 100%;
    margin-top: 12px;
    padding: 13px;
    color: $main-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    background: #fff;
    border: 1.4px dashed #c7cdda;
    border-radius: 11px;
    transition: 0.15s;

    &:hover {
        background: $bcg-card-new;
        border-color: $main-new;
    }

    &:disabled {
        opacity: 0.55;
        cursor: default;
    }
}

.ch-add-btn__icon ::v-deep .v-icon {
    color: $main-new !important;
}

.dr-link-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 11px 17px;
    color: #fff;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 12.5px;
    font-weight: 700;
    text-decoration: none;
    background: $main-new;
    border: 0;
    border-radius: 9px;
    box-shadow: 0 2px 10px rgba(82, 90, 213, 0.26);
    cursor: pointer;
    transition: 0.15s;
}

.dr-link-btn:hover {
    background: #454cc0;
    transform: translateY(-1px);
}

.dr-link-btn__icon ::v-deep .v-icon {
    color: #fff !important;
}
</style>
